import { ChangeDetectionStrategy, Component, computed, inject, output } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { environment } from '../../../environments/environment';
import { OrganizationPermissions } from '../../core/models/organization.model';
import { ROLE_LABELS, RoleService } from '../../core/services/role.service';
import { TenderService } from '../../core/services/tender.service';

interface NavItem {
  label: string;
  link: string;
  icon: string;
  exact?: boolean;
  count?: 'saved';
}

/** Exported for testing — the German labels are asserted on directly rather than through a rendered sidebar (no TestBed in this project; the class itself calls `inject()` at field-initializer time, which needs an Angular injection context). */
export const SIDEBAR_GROUPS: { title: string; items: NavItem[] }[] = [
  {
    title: 'ÜBERSICHT',
    items: [
      { label: 'Ausschreibungen', link: '/discover', exact: true, icon: 'M7 12.5a5.5 5.5 0 100-11 5.5 5.5 0 000 11zM11 11l3.5 3.5' },
      { label: 'Merkliste', link: '/tenders/saved', count: 'saved', icon: 'M4 2h8v12l-4-3-4 3V2z' },
    ],
  },
  {
    title: 'ANALYSE',
    items: [
      { label: 'Analysen', link: '/analyses', exact: true, icon: 'M2 13V8M6 13V3M10 13V6M14 13V9' },
      { label: 'Neue Analyse', link: '/analyses/new', icon: 'M8 3v10M3 8h10' },
    ],
  },
  {
    title: 'UNTERNEHMEN',
    items: [{ label: 'Unternehmensprofil', link: '/company', icon: 'M2 14V4l6-2 6 2v10M6 14V9h4v5M5 6h1M10 6h1' }],
  },
];

/** Organization administration, only shown to roles that hold the matching permission. */
export const SIDEBAR_ADMINISTRATION: { label: string; link: string; icon: string; exact?: boolean; permission: keyof OrganizationPermissions }[] = [
  { label: 'Organisation', link: '/organization', exact: true, permission: 'manageOrganization', icon: 'M2 14V4l6-2 6 2v10M6 14V9h4v5M5 6h1M10 6h1' },
  { label: 'Benutzer', link: '/organization/users', permission: 'manageUsers', icon: 'M6 7a2.5 2.5 0 100-5 2.5 2.5 0 000 5zM1.5 14a4.5 4.5 0 019 0M11 3.5a2.5 2.5 0 010 4M13 10a4 4 0 012.5 4' },
  { label: 'Benachrichtigungen', link: '/organization/notifications', permission: 'manageNotifications', icon: 'M4 11V7a4 4 0 118 0v4l1 2H3zM6.5 14a1.5 1.5 0 003 0' },
  { label: 'Tarif & Nutzung', link: '/organization/subscription', permission: 'viewSubscription', icon: 'M2 5h12v8H2zM2 8h12M5 11h2' },
  { label: 'Daten & Datenschutz', link: '/organization/privacy', permission: 'managePrivacy', icon: 'M8 1.5l5.5 2v4c0 3.2-2.3 5.6-5.5 7-3.2-1.4-5.5-3.8-5.5-7v-4l5.5-2z' },
];

export const SIDEBAR_BOTTOM: NavItem[] = [
  { label: 'Einstellungen', link: '/settings', icon: 'M8 10.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5zM8 1.5v2M8 12.5v2M1.5 8h2M12.5 8h2M3.4 3.4l1.4 1.4M11.2 11.2l1.4 1.4M3.4 12.6l1.4-1.4M11.2 4.8l1.4-1.4' },
  { label: 'Konto', link: '/account', icon: 'M8 8a3 3 0 100-6 3 3 0 000 6zM2.5 14a5.5 5.5 0 0111 0' },
];

@Component({
  selector: 'tc-sidebar',
  imports: [RouterLink, RouterLinkActive],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <a class="brand" routerLink="/discover" (click)="navigated.emit()">
      <span class="mark" aria-hidden="true">
        <svg viewBox="0 0 20 20" width="20" height="20"><rect width="20" height="20" rx="5" fill="currentColor"/><path d="M5.5 10.5l3 3 6-7" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </span>
      <span class="word">Vergabe-<b>Suche</b></span>
    </a>

    <nav aria-label="Hauptnavigation">
      @for (group of visibleGroups(); track group.title) {
        <div class="group-title">{{ group.title }}</div>
        @for (item of group.items; track item.link) {
          <a class="nav-item" [routerLink]="item.link" routerLinkActive="active" [routerLinkActiveOptions]="{ exact: !!item.exact }" (click)="navigated.emit()">
            <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path [attr.d]="item.icon"/></svg>
            <span>{{ item.label }}</span>
            @if (item.count === 'saved') {
              <span class="count">{{ tenders.savedCount() }}</span>
            }
          </a>
        }
      }
    </nav>

    <div class="spacer"></div>

    <nav aria-label="Konto" class="bottom">
      @if (devSwitcher) {
        <a class="role" routerLink="/account" title="Entwickler-Rollenumschalter">Angezeigt als {{ roleLabel() }}</a>
      }
      @for (item of bottom; track item.link) {
        <a class="nav-item" [routerLink]="item.link" routerLinkActive="active" (click)="navigated.emit()">
          <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path [attr.d]="item.icon"/></svg>
          <span>{{ item.label }}</span>
        </a>
      }
    </nav>
  `,
  styles: `
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      padding: 16px 12px;
      background: var(--subtle);
      border-right: 1px solid var(--border);
    }
    .brand {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 4px 10px 20px;
      color: var(--brand);
    }
    .word {
      color: var(--text-1);
      font-size: 15px;
      font-weight: 500;
      letter-spacing: -0.01em;
      b {
        font-weight: 600;
      }
    }
    .mark {
      display: inline-flex;
    }
    .group-title {
      padding: 14px 10px 6px;
      font-size: 11px;
      font-weight: 500;
      letter-spacing: 0.06em;
      color: var(--text-3);
    }
    .nav-item {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 7px 10px;
      border-radius: 6px;
      color: var(--text-2);
      &:hover {
        color: var(--text-1);
      }
      &.active {
        background: var(--surface);
        box-shadow: 0 0 0 1px var(--border);
        color: var(--text-1);
        font-weight: 500;
      }
      .count {
        margin-left: auto;
        font-family: var(--font-mono);
        font-size: 12px;
        color: var(--text-3);
      }
    }
    .spacer {
      flex: 1;
    }
    .role {
      display: block;
      padding: 4px 10px 8px;
      font-size: 11px;
      color: var(--text-3);
    }
    .bottom {
      border-top: 1px solid var(--border);
      padding-top: 8px;
    }
  `,
})
export class Sidebar {
  protected readonly tenders = inject(TenderService);
  private readonly roles = inject(RoleService);
  protected readonly devSwitcher = environment.devRoleSwitcher;
  protected readonly roleLabel = computed(() => ROLE_LABELS[this.roles.role()]);
  readonly navigated = output<void>();

  protected readonly groups = SIDEBAR_GROUPS;
  private readonly administration = SIDEBAR_ADMINISTRATION;
  protected readonly bottom = SIDEBAR_BOTTOM;

  protected readonly visibleGroups = computed<{ title: string; items: NavItem[] }[]>(() => {
    const items: NavItem[] = this.administration
      .filter((i) => this.roles.permissions()[i.permission])
      .map(({ label, link, icon, exact }) => ({ label, link, icon, exact }));
    return items.length ? [...this.groups, { title: 'ADMINISTRATION', items }] : this.groups;
  });
}
